import React from 'react';
import { Link } from 'react-router-dom';

const CheckIcon = () => (
  <svg className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
  </svg>
);

const ExpertiseIcon = () => (
  <svg className="h-6 w-6 text-apn-blue mr-3 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const services = [
  {
    title: 'Business Development',
    items: [
      'Strategic partnership development',
      'Market expansion strategies',
      'Revenue growth optimization',
      'Client acquisition strategies',
      'CRM systems design, rollouts & integrations',
    ],
  },
  {
    title: 'Marketing Consulting',
    items: [
      'Digital marketing strategy',
      'Brand positioning & messaging',
      'Content marketing campaigns',
      'Social media strategy',
    ],
  },
  {
    title: 'Website Development',
    items: [
      'Custom web applications',
      'Responsive design & development',
      'E-commerce solutions',
      'Database integration & APIs',
    ],
  },
  {
    title: 'AI Integration & Build-outs',
    items: [
      'Custom AI chatbot development',
      'Workflow & process automation',
      'AI-powered analytics dashboards',
      'Intelligent agent deployment',
    ],
  },
];

const expertise = [
  {
    title: 'Business Development & Strategic Market Expansion',
    desc: 'Strategic partnerships, market analysis, and growth planning',
  },
  {
    title: 'Marketing Strategy & Campaign Design',
    desc: 'Content creation, digital marketing, and brand development',
  },
  {
    title: 'CRM System Integration',
    desc: 'Client relationship management and engagement optimization',
  },
  {
    title: 'Website Development',
    desc: 'React, JavaScript, HTML, CSS, and modern frameworks',
  },
  {
    title: 'AI Integration & Automation',
    desc: 'Custom AI agents, chatbots, workflow automation, and intelligent analytics',
  },
  {
    title: 'Data Analytics & Performance Optimization',
    desc: 'Business growth metrics and performance tracking',
  },
];

const AdditionalServices = () => {
  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">Additional Services</h1>
            <p className="mt-2 text-xl">Professional Business Development & Technology Solutions</p>
          </div>
        </div>
      </div>

      {/* Services Overview */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Beyond our networking platform, we offer specialized consulting services to help your business grow and succeed in today's competitive market.
            </p>
          </div>

          {/* Services Grid — 4 cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {services.map((service) => (
              <div key={service.title} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-apn-blue text-white p-6">
                  <h3 className="text-xl font-bold">{service.title}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    {service.items.map((item) => (
                      <li key={item} className="flex items-start">
                        <CheckIcon />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Preview Portal CTA */}
      <section className="py-10 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-apn-blue mb-3">Already in contact with our team?</h2>
          <p className="text-gray-600 mb-6">
            If you've completed a demo with our team, log in to view your site preview and project status.
          </p>
          <Link
            to="/client/login"
            className="inline-flex items-center px-6 py-3 bg-apn-blue text-white font-medium rounded-lg hover:bg-apn-dark-blue transition-colors"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Client Portal Login
          </Link>
        </div>
      </section>

      {/* Meet Our Consultants */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Consultants</h2>
            <p className="text-xl text-gray-600 mb-6">Business Development, Marketing & Digital Solutions Specialists</p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-8">
                <div className="mb-8">
                  <p className="text-lg text-gray-700 leading-relaxed mb-6">
                    Our team of consultants brings extensive expertise in business development, marketing, website development, and AI integration, delivering innovative solutions to drive growth and elevate brand impact. Combining strategic insight with technical proficiency, they craft data-driven marketing campaigns, foster high-value partnerships, build modern user-centric websites, and deploy intelligent AI agents.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    Their work includes developing full-stack web applications, building custom AI chatbots and automation workflows, and optimizing client engagement through CRM systems — ensuring seamless alignment between business objectives and digital innovation.
                  </p>
                </div>

                {/* Nicholas Agouras Profile */}
                <div className="border-t border-gray-200 pt-8 mb-8">
                  <div className="md:flex items-start">
                    <div className="md:w-1/3 mb-6 md:mb-0 md:pr-8">
                      <img
                        src="/apn-logos/nicholas-agouras.jpg"
                        alt="Nicholas Agouras"
                        className="rounded-lg shadow-md w-full"
                      />
                      <div className="mt-4">
                        <h3 className="text-2xl font-bold text-gray-900">Nicholas Agouras</h3>
                        <p className="text-apn-blue font-semibold">CTO & Head of AI & Software Development</p>
                        <p className="text-sm text-gray-600 mt-1">nagouras@the-apn.com</p>
                        <p className="text-sm text-gray-600">(203) 460-2492</p>
                        <a href="https://www.linkedin.com/in/nicholas-agouras-b1457092" target="_blank" rel="noopener noreferrer" className="text-apn-blue hover:underline text-sm mt-1 inline-block">LinkedIn Profile →</a>
                      </div>
                    </div>
                    <div className="md:w-2/3">
                      <p className="text-lg text-gray-700 leading-relaxed mb-4">
                        Nicholas Agouras leads AI and software development at APN, bringing a rare combination of engineering discipline and creative problem-solving to the team. A Rutgers University graduate with deep roots in civil and environmental engineering, Nicholas spent years managing complex infrastructure projects — from wastewater resilience programs to heavy civil construction — before channeling that same systematic thinking into software and AI.
                      </p>
                      <p className="text-lg text-gray-700 leading-relaxed mb-4">
                        At APN, Nicholas is the architect behind the platform's AI-powered engine: the automated site generation pipeline that builds custom websites for local businesses, the intelligent outreach systems that connect those businesses with opportunities they didn't know existed, and the agent orchestration framework that keeps the entire operation running around the clock. He's the person who turned the idea of "build the website before you even call them" into a working system that generates showcase-quality sites for dozens of industries, complete with real photography, industry-specific design systems, and local SEO built in.
                      </p>
                      <p className="text-lg text-gray-700 leading-relaxed">
                        Nicholas is driven by a simple belief: every local business — whether it's a 60-year-old barbershop or a brand-new landscaping company — deserves a professional online presence. His engineering background taught him to build things that work reliably at scale, and that's exactly what he does at APN.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Technical & Professional Expertise</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {expertise.map((item) => (
                      <div key={item.title} className="flex items-start">
                        <ExpertiseIcon />
                        <div>
                          <h4 className="font-semibold text-gray-900">{item.title}</h4>
                          <p className="text-gray-600 text-sm mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Business?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Contact us today to discuss how our consulting services can help accelerate your business growth and digital transformation.
          </p>
          <Link to="/about/contact" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Get Started Today
          </Link>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <style>{`
          @keyframes apnStepFadeIn {
            from { opacity: 0; transform: translateY(24px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .apn-how-step {
            opacity: 0;
            animation: apnStepFadeIn 0.7s ease-out forwards;
          }
          .apn-how-step-1 { animation-delay: 0.1s; }
          .apn-how-step-2 { animation-delay: 0.3s; }
          .apn-how-step-3 { animation-delay: 0.5s; }
          .apn-how-step-4 { animation-delay: 0.7s; }
          .apn-how-card {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 32px 24px;
            height: 100%;
            box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05);
            transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
          }
          .apn-how-card:hover {
            transform: translateY(-4px);
            border-color: #3d67ad;
            box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04);
          }
          .apn-how-connector {
            display: none;
          }
          @media (min-width: 1024px) {
            .apn-how-connector {
              display: flex;
              position: absolute;
              top: 64px;
              right: -16px;
              width: 32px;
              height: 2px;
              background: linear-gradient(to right, #3d67ad, #e5e7eb);
              align-items: center;
              justify-content: flex-end;
              z-index: 1;
            }
            .apn-how-connector::after {
              content: '';
              width: 0;
              height: 0;
              border-left: 8px solid #3d67ad;
              border-top: 6px solid transparent;
              border-bottom: 6px solid transparent;
            }
          }
        `}</style>
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4" style={{ color: '#1f2937' }}>How It Works</h2>
          <p className="text-center mb-12 max-w-2xl mx-auto" style={{ color: '#6b7280' }}>From consultation to go-live in one week</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Step 1 */}
            <div className="apn-how-step apn-how-step-1" style={{ position: 'relative' }}>
              <div className="apn-how-card">
                <div style={{ fontSize: '12px', color: '#3d67ad', fontWeight: '700', letterSpacing: '0.1em', marginBottom: '12px' }}>STEP 01</div>
                <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: 'rgba(61, 103, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3d67ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>Book a Free Consultation</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>Schedule a no-obligation call to discuss your goals, brand, and business needs.</p>
              </div>
              <div className="apn-how-connector"></div>
            </div>

            {/* Step 2 */}
            <div className="apn-how-step apn-how-step-2" style={{ position: 'relative' }}>
              <div className="apn-how-card">
                <div style={{ fontSize: '12px', color: '#3d67ad', fontWeight: '700', letterSpacing: '0.1em', marginBottom: '12px' }}>STEP 02</div>
                <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: 'rgba(61, 103, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3d67ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>We Build Your Custom Demo</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>Our team designs and codes a custom demo site tailored to your business.</p>
              </div>
              <div className="apn-how-connector"></div>
            </div>

            {/* Step 3 */}
            <div className="apn-how-step apn-how-step-3" style={{ position: 'relative' }}>
              <div className="apn-how-card">
                <div style={{ fontSize: '12px', color: '#3d67ad', fontWeight: '700', letterSpacing: '0.1em', marginBottom: '12px' }}>STEP 03</div>
                <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: 'rgba(61, 103, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3d67ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>Review and Approve</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>Explore your new website, request revisions, and approve the final design.</p>
              </div>
              <div className="apn-how-connector"></div>
            </div>

            {/* Step 4 */}
            <div className="apn-how-step apn-how-step-4" style={{ position: 'relative' }}>
              <div className="apn-how-card">
                <div style={{ fontSize: '12px', color: '#3d67ad', fontWeight: '700', letterSpacing: '0.1em', marginBottom: '12px' }}>STEP 04</div>
                <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: 'rgba(61, 103, 173, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#3d67ad" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
                    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
                    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
                    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
                  </svg>
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>Go Live — We Handle Everything</h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>Domain, hosting, email, and deployment — we take care of the entire launch.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-apn-blue mb-4">Simple, Transparent Pricing</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every plan includes a custom-designed website, professional email, domain registration, and ongoing support. No hidden fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Starter */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 flex flex-col">
              <div className="bg-apn-blue text-white p-6">
                <h3 className="text-xl font-bold">Starter</h3>
                <div className="mt-3">
                  <span className="text-4xl font-bold">$749</span>
                  <span className="text-apn-light-blue text-sm ml-1">setup</span>
                </div>
                <div className="text-apn-light-blue text-sm mt-1">+ $79/month</div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <ul className="space-y-3 flex-1">
                  {['Custom interactive website', 'Professional email (2 M365 mailboxes)', 'Custom domain registration', 'SEO schema + sitemap', 'Analytics tracking', 'Mobile-responsive design', 'Monthly maintenance', 'AI skill hooks'].map((f, i) => (
                    <li key={i} className="flex items-start">
                      <CheckIcon />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/about/contact?tier=starter" className="mt-6 inline-flex justify-center items-center px-6 py-3 bg-apn-blue text-white font-medium rounded-lg hover:bg-apn-dark-blue transition-colors">
                  Book a Consultation
                </Link>
              </div>
            </div>

            {/* Professional — Most Popular */}
            <div className="bg-white rounded-lg shadow-xl overflow-hidden border-2 border-apn-blue flex flex-col relative transform lg:-translate-y-2">
              <div className="absolute top-0 left-0 right-0 bg-apn-blue text-white text-center py-1 text-xs font-bold uppercase tracking-wider">
                Most Popular
              </div>
              <div className="bg-apn-blue text-white p-6 pt-10">
                <h3 className="text-xl font-bold">Professional</h3>
                <div className="mt-3">
                  <span className="text-4xl font-bold">$1,250</span>
                  <span className="text-apn-light-blue text-sm ml-1">setup</span>
                </div>
                <div className="text-apn-light-blue text-sm mt-1">+ $199/month</div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-xs text-gray-500 font-semibold mb-3 uppercase tracking-wide">Everything in Starter, plus:</p>
                <ul className="space-y-3 flex-1">
                  {['AI chatbot widget (live on site)', 'Review management (Google/Yelp)', 'Booking calendar integration', 'Analytics dashboard', '4 M365 mailboxes', 'Priority support'].map((f, i) => (
                    <li key={i} className="flex items-start">
                      <CheckIcon />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/about/contact?tier=professional" className="mt-6 inline-flex justify-center items-center px-6 py-3 bg-apn-blue text-white font-medium rounded-lg hover:bg-apn-dark-blue transition-colors">
                  Book a Consultation
                </Link>
              </div>
            </div>

            {/* Premium */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 flex flex-col">
              <div className="bg-apn-blue text-white p-6">
                <h3 className="text-xl font-bold">Premium</h3>
                <div className="mt-3">
                  <span className="text-4xl font-bold">$1,997</span>
                  <span className="text-apn-light-blue text-sm ml-1">setup</span>
                </div>
                <div className="text-apn-light-blue text-sm mt-1">+ $399/month</div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-xs text-gray-500 font-semibold mb-3 uppercase tracking-wide">Everything in Professional, plus:</p>
                <ul className="space-y-3 flex-1">
                  {['Dedicated AI phone agent', 'Email drip campaigns', 'Social media integration', 'Full SEO + monthly reports', 'Unlimited M365 mailboxes', 'Dedicated AI account manager'].map((f, i) => (
                    <li key={i} className="flex items-start">
                      <CheckIcon />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/about/contact?tier=premium" className="mt-6 inline-flex justify-center items-center px-6 py-3 bg-apn-blue text-white font-medium rounded-lg hover:bg-apn-dark-blue transition-colors">
                  Book a Consultation
                </Link>
              </div>
            </div>

            {/* Enterprise */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200 flex flex-col">
              <div className="bg-apn-blue text-white p-6">
                <h3 className="text-xl font-bold">Enterprise</h3>
                <div className="mt-3">
                  <span className="text-3xl font-bold">Custom</span>
                </div>
                <div className="text-apn-light-blue text-sm mt-1">pricing tailored to you</div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-xs text-gray-500 font-semibold mb-3 uppercase tracking-wide">Everything in Premium, plus:</p>
                <ul className="space-y-3 flex-1">
                  {['Full AI business operating system', 'Custom AI agents per department', 'Custom branded voice AI', 'Live design sessions', 'Compliance monitoring', 'Dedicated infrastructure', 'White-label option'].map((f, i) => (
                    <li key={i} className="flex items-start">
                      <CheckIcon />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/about/contact?tier=enterprise" className="mt-6 inline-flex justify-center items-center px-6 py-3 bg-apn-blue text-white font-medium rounded-lg hover:bg-apn-dark-blue transition-colors">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-10">
            All plans include professional email setup, custom domain, and ongoing maintenance. No hidden fees. Cancel anytime after initial term.
          </p>
        </div>
      </section>
    </div>
  );
};

export default AdditionalServices;
